import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from '../../utils/debounce';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Card,
    CardContent,
    Alert,
    InputAdornment,
    useMediaQuery,
    useTheme,
    tooltipClasses,
    TooltipProps,
} from '@mui/material';
import { Edit, Delete, Search, Flag } from '@mui/icons-material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import api from '../../api/axios';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

interface PoliticalParty {
    id: number;
    name: string;
    color_code: string | null;
    created_at: string;
}

const PoliticalParties = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const queryClient = useQueryClient();

    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [open, setOpen] = useState(false);
    const [selectedParty, setSelectedParty] = useState<PoliticalParty | null>(null);
    const [name, setName] = useState('');
    const [colorCode, setColorCode] = useState('#000000');
    const [error, setError] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [partyToDelete, setPartyToDelete] = useState<PoliticalParty | null>(null);

    // Fetch Political Parties
    const { data, isLoading } = useQuery({
        queryKey: ['political-parties', pageModel.page, pageModel.pageSize, debouncedSearch],
        queryFn: async () => {
            const params: any = { page: pageModel.page + 1, per_page: pageModel.pageSize };
            if (debouncedSearch) params.search = debouncedSearch;
            const response = await api.get('/superadmin/political-parties', { params });
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    // Create/Update mutation
    const saveMutation = useMutation({
        mutationFn: async (data: { name: string; color_code: string }) => {
            if (selectedParty) {
                return api.put(`/superadmin/political-parties/${selectedParty.id}`, data);
            }
            return api.post('/superadmin/political-parties', data);
        },
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['political-parties'] });
            handleClose();
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message);
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            return api.delete(`/superadmin/political-parties/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['political-parties'] });
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message);
        },
    });

    const handleOpen = (party?: PoliticalParty) => {
        if (party) {
            setSelectedParty(party);
            setName(party.name);
            setColorCode(party.color_code || '#000000');
        } else {
            setSelectedParty(null);
            setName('');
            setColorCode('#000000');
        }
        setError('');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedParty(null);
        setName('');
        setColorCode('#000000');
        setError('');
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        saveMutation.mutate({ name, color_code: colorCode });
    };

    const handleDeleteClick = (party: PoliticalParty) => {
        setPartyToDelete(party);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (partyToDelete) {
            deleteMutation.mutate(partyToDelete.id);
        }
        setDeleteDialogOpen(false);
        setPartyToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setPartyToDelete(null);
    };

    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Sr. No',
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (pageModel.page * pageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },
        {
            field: 'name',
            headerName: 'Party Name',
            flex: 1,
            minWidth: 200,
            renderCell: (params: GridRenderCellParams) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: params.row.color_code || '#ccc',
                        border: '2px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                    }} />
                    <Typography variant="body2" fontWeight={500}>{params.value}</Typography>
                </Box>
            ),
        },
        {
            field: 'created_at',
            headerName: 'Created',
            width: 120,
            valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A',
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'green' }}>
                        <BootstrapTooltip title="Edit" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleOpen(params.row)}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'red' }}>
                        <BootstrapTooltip title="Delete" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleDeleteClick(params.row)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                </Box>
            ),
        },
    ];

    // Mobile columns
    const mobileColumns: GridColDef[] = [
        { field: 'name', headerName: 'Party Name', flex: 1 },
        {
            field: 'actions',
            headerName: '',
            width: 80,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <IconButton size="small" onClick={() => handleOpen(params.row)}>
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
                        <Delete fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    const rows = data?.data?.data || data?.data || [];
    const rowCount = data?.data?.total || data?.total || 0;

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h2">Political Parties</Typography>
            </Box>

            {/* Search */}
            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <TextField
                        sx={{ width: '30%', borderRadius: 1 }}
                        size="small"
                        placeholder="Search parties..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button variant="contained" startIcon={<Flag />} onClick={() => handleOpen()}>
                        Add Party
                    </Button>
                </CardContent>
            </Card>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* DataGrid */}
            <Card>
                <DataGrid
                    rows={rows}
                    columns={isMobile ? mobileColumns : columns}
                    loading={isLoading}
                    paginationModel={pageModel}
                    onPaginationModelChange={setPageModel}
                    pageSizeOptions={[10, 25, 50]}
                    paginationMode="server"
                    rowCount={rowCount}
                    autoHeight
                    disableRowSelectionOnClick
                    sx={{ border: 0 }}
                />
            </Card>

            {/* Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle fontSize={22}>{selectedParty ? 'Edit Party' : 'Add Party'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        <TextField
                            label="Party Name"
                            fullWidth
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <Box>
                            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>Party Color</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: 48,
                                        height: 48,
                                        borderRadius: '50%',
                                        bgcolor: colorCode,
                                        border: '3px solid',
                                        borderColor: 'divider',
                                        cursor: 'pointer',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <input
                                        type="color"
                                        value={colorCode}
                                        onChange={(e) => setColorCode(e.target.value)}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            opacity: 0,
                                            cursor: 'pointer',
                                        }}
                                    />
                                </Box>
                                <TextField
                                    size="small"
                                    value={colorCode}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) setColorCode(val);
                                    }}
                                    sx={{ width: 120 }}
                                    placeholder="#000000"
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                        <Button type="submit" color="success" variant="contained" disabled={saveMutation.isPending}>
                            {saveMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3, overflow: 'hidden' }
                }}
            >
                <Box sx={{
                    bgcolor: '#fee2e2', p: 3, display: 'flex', alignItems: 'center', gap: 2,
                    borderBottom: '1px solid #fecaca'
                }}>
                    <Box sx={{
                        bgcolor: '#dc2626', borderRadius: '50%', p: 1.5,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>Confirm Delete</Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>This action cannot be undone</Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to delete <strong>"{partyToDelete?.name}"</strong>?
                    </Typography>
                    <Alert severity="warning" sx={{
                        bgcolor: '#fef3c7', border: '1px solid #fcd34d',
                        '& .MuiAlert-icon': { color: '#d97706' }
                    }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Warning: You will not be able to revert this action!
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button variant="outlined" onClick={handleDeleteCancel}
                        sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm}
                        disabled={deleteMutation.isPending} startIcon={<Delete />}
                        sx={{
                            borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600,
                            bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }
                        }}>
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PoliticalParties;
